import {
  inspectProductQuantities,
  fetchOrderSourceFileText,
  type FlaggedProduct,
  type FlaggedProductWithRestaurant,
  type ProductInspectionRow,
  type ProductInspectionResult,
  type OrderhistoryInspectionRow,
  type FactuurInspectionRow,
  type ProductInventoryData,
  type ProductCostPriceData,
} from '../../../../../services/admin/adminService';

export function formatQuantityInfo(qi: FlaggedProduct['quantityInfo']): string {
  if (!qi) return 'null';
  const base = `${qi.stdMultiplier} x ${qi.stdAmount}${qi.stdUnit}`;
  return qi.weightString ? `${base} (ws: "${qi.weightString}")` : base;
}

// Renders one markdown table, or a "(none)" line under the same label when there are no rows.
function mdTable(label: string, headers: string[], rows: string[][]): string {
  if (rows.length === 0) return `${label}: (none)`;
  const head = `| ${headers.join(' | ')} |`;
  const sep = `|${headers.map(() => '---').join('|')}|`;
  return [`${label}:`, head, sep, ...rows.map(r => `| ${r.join(' | ')} |`)].join('\n');
}

// Renders the INVENTORY DATA section of the fix prompt: the "(not in inventory)" line, or one table
// per product_key surface, so the receiving agent knows the quantity issue also froze into saved
// state (none of which heals when orderhistory.quantityInfo is fixed). All FOUR surfaces are shown —
// assignments, frozen counts, minimums (M63) and price overrides (M69); the last two are
// std-unit-denominated and used to be invisible here.
// Internal super-admin tool — plain English strings match the rest of this hardcoded page.
function buildInventoryDataSection(inv: ProductInventoryData | undefined): string {
  const assignments = inv?.inventoryAssignments ?? [];
  const items = inv?.inventoryCountItems ?? [];
  const minimums = inv?.inventoryMinimums ?? [];
  const overrides = inv?.inventoryPriceOverrides ?? [];
  if (assignments.length === 0 && items.length === 0 && minimums.length === 0 && overrides.length === 0) {
    return '(no inventory rows matched this product\'s keys. If the ORDERHISTORY/FACTUUR tables above DO have rows, this genuinely is not an inventory product — skip STEP 3. If they are ALSO empty, no product identity could be resolved at all: do NOT conclude anything about inventory, re-check the product name/platform/articleId first.)';
  }

  const total = inv?.inventoryCountItemsTotalMatches ?? items.length;
  const capNote = total > items.length ? ` (showing latest ${items.length} of ${total})` : '';

  return [
    mdTable(
      'Inventory assignments',
      ['productKey', 'restaurant', 'section', 'count_unit', 'count_unit_to_std_factor', 'std_unit', 'additional_count_units'],
      assignments.map(a => [
        a.productKey, a.restaurantName ?? '', a.sectionName ?? '', a.countUnit ?? '',
        String(a.countUnitToStdFactor ?? ''), a.stdUnit ?? '',
        a.additionalCountUnits ? JSON.stringify(a.additionalCountUnits) : '',
      ]),
    ),
    mdTable(
      `Frozen count items${capNote}`,
      ['counted_at', 'restaurant', 'section', 'productKey', 'count', 'countUnit', 'stdUnit', 'countInStdUnit', 'pricePerStdUnit', 'value'],
      items.map(i => [
        i.countedAt ?? '', i.restaurantName ?? '', i.sectionNameSnapshot ?? '', i.productKey,
        String(i.count ?? ''), i.countUnit ?? '', i.stdUnit ?? '', String(i.countInStdUnit ?? ''),
        String(i.pricePerStdUnit ?? ''), String(i.value ?? ''),
      ]),
    ),
    mdTable(
      'Product minimums (M63 — min_stock_in_std_unit is in the OLD std unit if stdUnit changes)',
      ['productKey', 'restaurant', 'min_stock_count', 'count_unit', 'min_stock_in_std_unit'],
      minimums.map(m => [
        m.productKey, m.restaurantName ?? '', String(m.minStockCount ?? ''), m.countUnit ?? '',
        String(m.minStockInStdUnit ?? ''),
      ]),
    ),
    mdTable(
      'Manual price overrides (M69 — price_per_std_unit is in the OLD std unit if stdUnit changes)',
      ['productKey', 'restaurant', 'price_per_std_unit', 'std_unit'],
      overrides.map(o => [
        o.productKey, o.restaurantName ?? '', String(o.pricePerStdUnit ?? ''), o.stdUnit ?? '',
      ]),
    ),
  ].join('\n\n');
}

// True when a stored snapshot is the SAME measured basis as the flagged (bad) quantityInfo — the
// signal fixQuantities.md STEP 4 requires before converting a row. Compared field-by-field on the
// three basis fields only; weightString is display text and drifts independently.
// A MISSING quantityInfo is itself a basis: "quantityInfo is null" is a common flag reason, and the
// ingredients set up against it snapshotted nothing either — those rows do need converting.
function snapshotMatchesFlag(snapshot: unknown, flagged: FlaggedProduct['quantityInfo']): boolean {
  const snapshotIsEmpty = !snapshot || typeof snapshot !== 'object';
  if (!flagged) return snapshotIsEmpty;
  if (snapshotIsEmpty) return false;
  const s = snapshot as Record<string, unknown>;
  return (
    String(s.stdUnit ?? '') === String(flagged.stdUnit ?? '') &&
    Number(s.stdAmount) === Number(flagged.stdAmount) &&
    Number(s.stdMultiplier) === Number(flagged.stdMultiplier)
  );
}

// Renders the COST PRICE REFERENCES section of the fix prompt: either the "(not referenced)" line, or a
// markdown table of every cost-price ingredient / alternative / subrecipe ingredient pointing at this
// product. Those rows SNAPSHOT quantityInfo and express quantityNeeded in that unit, so fixing the order
// data without converting them silently changes the dish math (fixQuantities.md STEP 4).
//
// The lookup matches by product IDENTITY, not by snapshot — so `matches_flagged_snapshot` is computed
// here and is the column that decides whether a row gets converted. Rows marked false reference the
// same product but were set up against a different (often already-correct) basis: leave them alone.
// Internal super-admin tool — plain English strings match the rest of this hardcoded page.
function buildCostPriceDataSection(
  cp: ProductCostPriceData | undefined,
  flagged: FlaggedProduct['quantityInfo'],
): string {
  const refs = cp?.costPriceRefs ?? [];
  if (refs.length === 0) {
    return '(product not referenced by any cost price item or subrecipe)';
  }

  const total = cp?.costPriceRefsTotalMatches ?? refs.length;
  const capNote = total > refs.length ? ` (showing ${refs.length} of ${total})` : '';
  return mdTable(
    `Cost price references${capNote}`,
    ['ref_kind', 'restaurant', 'cost price / subrecipe', 'productName', 'platform', 'articleId',
      'quantityNeeded', 'quantityInfo (snapshot)', 'matches_flagged_snapshot', 'custom'],
    refs.map(r => [
      r.refKind, r.restaurantName ?? '', r.ownerName ?? '', r.productName ?? '', r.platform ?? '',
      r.articleId ?? '', String(r.quantityNeeded ?? ''),
      r.quantityInfo === null || r.quantityInfo === undefined ? 'null' : JSON.stringify(r.quantityInfo),
      String(snapshotMatchesFlag(r.quantityInfo, flagged)),
      String(r.isCustom),
    ]),
  );
}

// Renders one inspection block as a markdown table with a shared prefix + any extra columns per row.
function buildInspectionTable<T extends ProductInspectionRow>(
  rows: T[],
  emptyLine: string,
  extraCols: { header: string; value: (r: T) => string }[],
): string {
  if (rows.length === 0) return emptyLine;
  const baseHeaders = ['name', 'articleId', 'price', 'quantity', 'rawQuantity', 'quantityInfo', 'occurrences'];
  const headerCells = [...baseHeaders, ...extraCols.map(c => c.header)];
  const header = `| ${headerCells.join(' | ')} |`;
  const sep = `|${headerCells.map(() => '---').join('|')}|`;
  const body = rows.map(r => {
    const qi = r.quantityInfo === null || r.quantityInfo === undefined ? 'null' : JSON.stringify(r.quantityInfo);
    const base = [r.productName ?? '', r.articleId ?? '', r.price ?? '', r.quantity ?? '', r.rawQuantity ?? '', qi, String(r.occurrences)];
    const extra = extraCols.map(c => c.value(r));
    return `| ${[...base, ...extra].join(' | ')} |`;
  });
  return [header, sep, ...body].join('\n');
}

export function buildFixQuantitiesPrompt(
  p: FlaggedProduct,
  dbRows: ProductInspectionResult['rows'],
  inventory?: ProductInventoryData,
  costPrice?: ProductCostPriceData,
): string {
  const rowInfo = [
    `Platform: ${p.platform}`,
    `Article ID: ${p.articleId || '-'}`,
    `Product: ${p.productName}`,
    `Source: ${p.source ?? 'pakbon'}`,
    `Quantity: ${p.quantity} | Raw Qty: ${p.rawQuantity} | Unit Price: €${p.unitPrice.toFixed(2)}`,
    `QuantityInfo: ${formatQuantityInfo(p.quantityInfo)}`,
    `Reason: ${p.reason}`,
    p.setupSource
      ? `Setup upload: restaurant ${p.setupSource.restaurantId}, file ${p.setupSource.fileId} (no email). Source ID: ${p.gmailId || '-'}`
      : `Gmail ID: ${p.gmailId || '-'}`,
  ].join('\n');

  const orderhistorySection = buildInspectionTable<OrderhistoryInspectionRow>(
    dbRows.orderhistory,
    '(no matching rows found in orderhistory)',
    [{ header: 'overridden_by_factuur', value: r => String(r.overriddenByFactuur) }],
  );

  const factuurSection = buildInspectionTable<FactuurInspectionRow>(
    dbRows.factuurOrders,
    '(no matching rows found in factuur_orders)',
    [
      { header: 'platform', value: r => r.platform ?? '' },
      { header: 'linked_to_order', value: r => String(r.linkedToOrder) },
    ],
  );

  return `@README.md @server/database/database_shemas.md @z_playbooks/roles/fixQuantities.md we have this new issue:
${rowInfo}

ORDERHISTORY (raw pakbon data):
${orderhistorySection}

FACTUUR_ORDERS (what the app actually reads where linked):
${factuurSection}

INVENTORY DATA (every product_key surface — assignments, frozen counts, minimums, price overrides; none of these heal when the order data is fixed):
${buildInventoryDataSection(inventory)}

COST PRICE REFERENCES (ingredients / alternatives / subrecipe ingredients referencing this product — convert ONLY rows where matches_flagged_snapshot = true and custom = false):
${buildCostPriceDataSection(costPrice, p.quantityInfo)}`;
}

// Builds the same fix-quantities prompt the copy button uses (case context + DB inspection),
// for sending directly to the code-monkey instead of the clipboard.
export async function buildQuantityCaseInfo(p: FlaggedProduct): Promise<string> {
  const { rows, inventory, costPrice } = await inspectProductQuantities(p.productName, {
    platform: p.platform,
    articleId: p.articleId,
  });
  return buildFixQuantitiesPrompt(p, rows, inventory, costPrice);
}

// The key a flag's source file is fetched and cached under: one email (rfc822 id) can hold both a
// pakbon and a factuur file, and `source` decides which one this flag must be fixed against.
// NOTE: never key on `restaurantId` — on a quantity flag that field is the email_audit_log row id.
const qtyFlagFileKey = (f: FlaggedProductWithRestaurant) => `${f.setupSource ? `setup:${f.setupSource.restaurantId}:${f.setupSource.fileId}` : f.gmailId}|${f.source ?? 'pakbon'}`;

// Builds the body of a batch code-monkey handoff: every flag's fix prompt grouped by the source
// file its order came from, with each file's raw extracted text included ONCE per group so the
// session fixes from what the parser actually saw instead of the flag description alone. Files
// are fetched once per unique (gmailId, source), then groups are merged on the resolved S3 URL —
// a factuur-back-created order shares its file with the pakbon side. Flags whose lookup returns
// nothing (pre-M31 orders, failed uploads) land in a trailing no-file group.
export async function buildGroupedQuantityCaseInfo(flags: FlaggedProductWithRestaurant[]): Promise<string> {
  const blocks = await Promise.all(flags.map(buildQuantityCaseInfo));

  // One fetch per unique email+source, chunked so a supplier-wide bulk send doesn't fire dozens of
  // S3-download + pdftotext requests at the server at once. Best-effort per email.
  const withGmail = flags.filter((f) => f.gmailId);
  const fetchKeys = Array.from(new Map(withGmail.map((f) => [qtyFlagFileKey(f), f])).entries());
  const fileByEmail = new Map<string, { fileText: string; sourceFileUrl: string }>();
  const FETCH_CHUNK = 5;
  for (let i = 0; i < fetchKeys.length; i += FETCH_CHUNK) {
    await Promise.all(fetchKeys.slice(i, i + FETCH_CHUNK).map(async ([key, f]) => {
      try {
        const file = await fetchOrderSourceFileText(f.gmailId!, f.source ?? 'pakbon', f.setupSource);
        if (file.fileText && file.sourceFileUrl) {
          fileByEmail.set(key, { fileText: file.fileText, sourceFileUrl: file.sourceFileUrl });
        }
      } catch (err) {
        console.error('Failed to fetch source file text for batch quantity flag', err);
      }
    }));
  }

  const groups = new Map<string, { fileText: string | null; indices: number[] }>();
  flags.forEach((f, i) => {
    const file = f.gmailId ? fileByEmail.get(qtyFlagFileKey(f)) : undefined;
    const groupKey = file ? `url|${file.sourceFileUrl}` : 'no-file';
    const group = groups.get(groupKey) ?? { fileText: file?.fileText ?? null, indices: [] };
    group.indices.push(i);
    groups.set(groupKey, group);
  });

  let issueNo = 0;
  const issueHeading = (i: number) => {
    issueNo += 1;
    return `### Issue ${issueNo} of ${flags.length} — ${flags[i].productName} (${flags[i].platform}, ${flags[i].restaurantName})`;
  };
  const fileGroups = Array.from(groups.entries()).filter(([key]) => key !== 'no-file').map(([, g]) => g);
  const sections = fileGroups.map((g, fi) => {
    const issues = g.indices.map((i) => `${issueHeading(i)}\n${blocks[i]}`).join('\n\n');
    return `## Source file ${fi + 1} of ${fileGroups.length} — ${g.indices.length} issue(s)\n\n${issues}\n\n`
      + `File text (raw extracted source document shared by the issue(s) above):\n${g.fileText}`;
  });
  const noFile = groups.get('no-file');
  if (noFile) {
    const issues = noFile.indices.map((i) => `${issueHeading(i)}\n${blocks[i]}`).join('\n\n');
    sections.push(`## Issues with no stored source file (older orders predate file storage)\n\n${issues}`);
  }
  return sections.join('\n\n---\n\n');
}
